/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Announced, IAnnouncedProps } from '@fluentui/react/lib/Announced';
import * as React from 'react';

/**
 * A hook used for Accessibility
 * to “announce” (through a screen reader) relevant information that is not conveyed through the DOM by default,
 * such as results of callback functions.
 * An example would be announcing successful addition, editing, deletion of rows in a table.
 * Spec can be found at:
 * https://microsoft-my.sharepoint-df.com/:w:/p/leugene/ER6-Zp06cd5IuNTyb_5MR7oBmoelMaiZDCVyefOXJpsdWg?e=LbwDvy
 * @param ariaLive Politeness. Default is "assertive". See: https://www.w3.org/TR/wai-aria/#aria-live
 * @returns { announcedComponent, announceAction }
 * announcedComponent needs to be injected in the JSX of the component that needs to announce something.
 * announceAction takes a string as a parameter and modifies announcedComponent, causing screen reader to read message.
 */

export const useAnnounced = (ariaLive: IAnnouncedProps['aria-live'] = 'assertive') => {
    const [announcedComponent, setAnnouncedComponent] = React.useState<React.ReactNode>(null);

    const announceAction = (newMessage: string) => {
        const uniqueKey = Date.now().toString();

        setAnnouncedComponent(<Announced key={uniqueKey} message={newMessage} aria-live={ariaLive} />);
    };

    return { announcedComponent, announceAction };
};
